import { useState, useEffect, useRef } from "react";
import api, { fmtMoney, errDetail, BACKEND_URL } from "../lib/api";
import CrudPage, { FieldInput } from "../components/CrudPage";
import { toast } from "sonner";
import { ImageOff, Upload } from "lucide-react";

const ImageUpload = ({ value, onChange }) => {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Resim yüklendi");
    } catch (e) { toast.error(errDetail(e)); }
    finally { setUploading(false); }
  };

  return (
    <div className="flex items-center gap-3">
      <div className="flex h-16 w-16 items-center justify-center overflow-hidden border border-gray-200 bg-gray-50">
        {value ? <img src={`${BACKEND_URL}${value}`} alt="Ürün" className="h-full w-full object-contain" /> : <ImageOff size={20} className="text-gray-300" />}
      </div>
      <button type="button" data-testid="product-image-upload" onClick={() => ref.current?.click()}
        className="flex items-center gap-2 border border-gray-200 px-4 py-2 text-sm transition-colors duration-200 hover:border-[#D4AF37]">
        <Upload size={15} /> {uploading ? "Yükleniyor..." : "Resim Seç"}
      </button>
      <input ref={ref} type="file" accept="image/*" className="hidden"
        onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
    </div>
  );
};

export default function Products() {
  const [categories, setCategories] = useState(null);
  const [subcategories, setSubcategories] = useState([]);

  useEffect(() => {
    api.get("/categories", { params: { limit: 200 } }).then((r) => setCategories(r.data.items));
    api.get("/subcategories", { params: { limit: 500 } }).then((r) => setSubcategories(r.data.items));
  }, []);

  if (!categories) return null;
  const catOptions = categories.map((c) => ({ value: c.id, label: c.name }));
  const subOptions = subcategories.map((s) => ({ value: s.id, label: `${s.name} (${s.category_name || "-"})` }));

  return (
    <CrudPage
      title="Ürünler"
      subtitle="Ürün kartlarını yönetin - brüt ve net kar otomatik hesaplanır"
      module="products"
      endpoint="/products"
      addLabel="Yeni Ürün"
      defaultValues={{ active: true, tax_rate: 0, stock: 0, min_stock: 0 }}
      filters={[
        { name: "category_id", label: "Ana Kategori", options: catOptions },
        { name: "subcategory_id", label: "Alt Kategori", options: subcategories.map((s) => ({ value: s.id, label: s.name })) },
      ]}
      columns={[
        {
          key: "image", label: "Resim",
          render: (r) => r.image
            ? <div className="flex h-10 w-10 items-center justify-center border border-gray-200 bg-gray-50"><img src={`${BACKEND_URL}${r.image}`} alt={r.name} className="h-full w-full object-contain" /></div>
            : <div className="flex h-10 w-10 items-center justify-center border border-gray-200 bg-gray-50"><ImageOff size={16} className="text-gray-300" /></div>,
        },
        { key: "name", label: "Ürün Adı", render: (r) => <span className="font-medium">{r.name}</span> },
        { key: "code", label: "Kod" },
        { key: "category_name", label: "Kategori", render: (r) => `${r.category_name || "-"}${r.subcategory_name ? " / " + r.subcategory_name : ""}` },
        { key: "brand", label: "Marka" },
        { key: "purchase_price", label: "Alış", render: (r) => fmtMoney(r.purchase_price) },
        { key: "sale_price", label: "Satış", render: (r) => <span className="font-semibold text-[#D4AF37]">{fmtMoney(r.sale_price)}</span> },
        { key: "gross_profit", label: "Brüt Kar", render: (r) => fmtMoney(r.gross_profit) },
        { key: "net_profit", label: "Net Kar", render: (r) => fmtMoney(r.net_profit) },
        {
          key: "stock", label: "Stok",
          render: (r) => <span className={Number(r.stock) <= Number(r.min_stock) ? "font-semibold text-red-500" : ""}>{r.stock} {r.unit || ""}</span>,
        },
        { key: "sort_order", label: "Sıra No (POS)", render: (r) => <span className="text-gray-500">{r.sort_order ?? "-"}</span> },
        {
          key: "active", label: "Durum",
          render: (r) => (
            <span className={`px-2 py-1 text-xs font-semibold ${r.active !== false ? "bg-emerald-50 text-emerald-600" : "bg-gray-100 text-gray-500"}`}>
              {r.active !== false ? "Aktif" : "Pasif"}
            </span>
          ),
        },
      ]}
      fields={[
        { name: "image", label: "Ürün Resmi", colSpan: 2, component: ImageUpload },
        { name: "name", label: "Ürün Adı", required: true },
        { name: "code", label: "Ürün Kodu" },
        { name: "category_id", label: "Ana Kategori", type: "select", options: catOptions, required: true },
        { name: "subcategory_id", label: "Alt Kategori", type: "select", options: subOptions },
        { name: "brand", label: "Marka" },
        { name: "unit", label: "Birim", placeholder: "Adet, Kg, Lt..." },
        { name: "purchase_price", label: "Alış Fiyatı (₺)", type: "number", required: true },
        { name: "sale_price", label: "Satış Fiyatı (₺)", type: "number", required: true },
        { name: "tax_rate", label: "Vergi Oranı (%)", type: "number" },
        { name: "stock", label: "Mevcut Stok", type: "number" },
        { name: "min_stock", label: "Minimum Stok", type: "number" },
        { name: "warehouse", label: "Depo" },
        {
          name: "sort_order", label: "Sıra No (POS Sıralaması)", type: "number",
          placeholder: "Boş bırakılırsa eklenme tarihine göre sıralanır",
        },
        { name: "description", label: "Açıklama", type: "textarea" },
        { name: "active", label: "Aktif", type: "checkbox" },
      ]}
      transformSubmit={(p) => ({
        ...p,
        category_name: categories.find((c) => c.id === p.category_id)?.name || "",
        subcategory_name: subcategories.find((s) => s.id === p.subcategory_id)?.name || "",
      })}
    />
  );
}

export { ImageUpload };
